/**
 * Asset Modal State Management Hook
 *
 * Manages the open/close state for all modals in the asset section.
 * This includes comment modal, tag modal, and status confirmation modal.
 *
 * This is a simple state management hook with no side effects or complex logic,
 * making it safe to extract and reuse.
 */

import { useState } from 'react';

import type { PartialAsset } from '@/hooks/useAssets';
import type { AssetStatus } from '@/types';

/**
 * State for status confirmation modal
 */
interface ConfirmStatusState {
  status: AssetStatus;
  data: PartialAsset[];
}

/**
 * Return type for useAssetModalState hook
 */
interface AssetModalStateReturn {
  // Comment modal
  isCommentModalOpen: boolean;
  setIsCommentModalOpen: (open: boolean) => void;

  // Tag modal
  isTagModalOpen: boolean;
  setIsTagModalOpen: (open: boolean) => void;

  // Remove tag modal
  isRemoveTagModalOpen: boolean;
  setIsRemoveTagModalOpen: (open: boolean) => void;

  // Export modal
  isExportModalOpen: boolean;
  setIsExportModalOpen: (open: boolean) => void;

  // Import modal
  isImportModalOpen: boolean;
  setIsImportModalOpen: (open: boolean) => void;

  // Get started modal (first-time user onboarding)
  isGetStartedModalOpen: boolean;
  setIsGetStartedModalOpen: (open: boolean) => void;

  // Add seed modal (opened from GetStartedModal seeds method)
  isAddSeedModalOpen: boolean;
  setIsAddSeedModalOpen: (open: boolean) => void;

  // Status confirmation modal
  confirmStatus: ConfirmStatusState | null;
  setConfirmStatus: (state: ConfirmStatusState | null) => void;
}

/**
 * Hook to manage modal state for asset section
 *
 * Provides state and setters for all modals used in the asset table:
 * - Comment modal for adding notes to assets
 * - Tag modal for adding tags
 * - Remove tag modal for removing tags
 * - Export modal for exporting asset data
 * - Import modal for importing asset data
 * - Status confirmation modal for bulk status changes
 *
 * @returns Object containing all modal states and their setters
 *
 * @example
 * ```tsx
 * const { isCommentModalOpen, setIsCommentModalOpen } = useAssetModalState();
 *
 * // Open comment modal
 * <Button onClick={() => setIsCommentModalOpen(true)}>Add Comment</Button>
 *
 * // Render modal
 * <CommentModal
 *   open={isCommentModalOpen}
 *   onClose={() => setIsCommentModalOpen(false)}
 * />
 * ```
 */
export const useAssetModalState = (): AssetModalStateReturn => {
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [isRemoveTagModalOpen, setIsRemoveTagModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isGetStartedModalOpen, setIsGetStartedModalOpen] = useState(false);
  const [isAddSeedModalOpen, setIsAddSeedModalOpen] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState<ConfirmStatusState | null>(
    null
  );

  return {
    isCommentModalOpen,
    setIsCommentModalOpen,
    isTagModalOpen,
    setIsTagModalOpen,
    isRemoveTagModalOpen,
    setIsRemoveTagModalOpen,
    isExportModalOpen,
    setIsExportModalOpen,
    isImportModalOpen,
    setIsImportModalOpen,
    isGetStartedModalOpen,
    setIsGetStartedModalOpen,
    isAddSeedModalOpen,
    setIsAddSeedModalOpen,
    confirmStatus,
    setConfirmStatus,
  };
};
